// src/services/adminUserService.ts

import { apiClient } from "./apiClient";
import { 
  AdminUserDto, 
  PageResponse, 
  AdminUserFilters,
  BulkOperationResponse 
} from "../types/admin.types";
import { UserStatusEnum } from "../models/enums/UserStatusEnum";
import { UserRoleEnum } from "../models/enums/UserRoleEnum";

const API_BASE_URL = "/api/admin/users";

/**
 * Build query string from filters
 */
const buildQueryString = (
  page: number,
  size: number,
  filters: AdminUserFilters
): string => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("size", size.toString());

  if (filters.status) params.append("status", filters.status);
  if (filters.role) params.append("role", filters.role);
  if (filters.search) params.append("search", filters.search);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortDir) params.append("sortDir", filters.sortDir);

  return params.toString();
};

/**
 * Get paginated list of admin users with filters
 */
export const getAdminUsers = async (
  page: number,
  size: number,
  filters: AdminUserFilters
): Promise<PageResponse<AdminUserDto>> => {
  const query = buildQueryString(page, size, filters);
  return apiClient.get<PageResponse<AdminUserDto>>(`${API_BASE_URL}?${query}`);
};

/**
 * Create a new user (ADMIN only)
 */
export const createUser = async (
  username: string,
  email: string,
  password: string,
  role?: UserRoleEnum
): Promise<AdminUserDto> => {
  return apiClient.post<AdminUserDto>(API_BASE_URL, { username, email, password, role });
};

/**
 * Change user password
 */
export const changeUserPassword = async (
  userId: number,
  newPassword: string
): Promise<AdminUserDto> => {
  return apiClient.put<AdminUserDto>(`${API_BASE_URL}/${userId}/password`, { newPassword });
};

/**
 * Change user role
 */
export const changeUserRole = async (
  userId: number,
  newRole: UserRoleEnum
): Promise<AdminUserDto> => {
  return apiClient.put<AdminUserDto>(`${API_BASE_URL}/${userId}/role`, { newRole });
};

/**
 * Change user lifecycle status
 */
export const changeUserStatus = async (
  userId: number,
  newStatus: UserStatusEnum
): Promise<AdminUserDto> => {
  return apiClient.put<AdminUserDto>(`${API_BASE_URL}/${userId}/status`, { newStatus });
};

/**
 * Change user username (ADMIN only)
 */
export const changeUserUsername = async (
  userId: number,
  newUsername: string
): Promise<AdminUserDto> => {
  return apiClient.put<AdminUserDto>(`${API_BASE_URL}/${userId}/username`, { newUsername });
};

/**
 * Change user email (ADMIN only)
 */
export const changeUserEmail = async (
  userId: number,
  newEmail: string
): Promise<AdminUserDto> => {
  return apiClient.put<AdminUserDto>(`${API_BASE_URL}/${userId}/email`, { newEmail });
};

/**
 * Toggle verified badge on a user account
 */
export const setUserVerified = async (
  userId: number,
  verified: boolean
): Promise<AdminUserDto> => {
  return apiClient.put<AdminUserDto>(`${API_BASE_URL}/${userId}/verified`, { verified });
};

/**
 * Suspend a user
 */
export const suspendUser = async (
  userId: number,
  days: number,
  reason?: string
): Promise<AdminUserDto> => {
  return apiClient.put<AdminUserDto>(`${API_BASE_URL}/${userId}/suspend`, { days, reason });
};

/**
 * Unsuspend a user
 */
export const unsuspendUser = async (userId: number): Promise<AdminUserDto> => {
  return apiClient.put<AdminUserDto>(`${API_BASE_URL}/${userId}/unsuspend`, {});
};

/**
 * Ban a user permanently
 */
export const banUser = async (userId: number, reason?: string): Promise<AdminUserDto> => {
  return apiClient.put<AdminUserDto>(`${API_BASE_URL}/${userId}/ban`, { reason });
};

/**
 * Unban a user
 */
export const unbanUser = async (userId: number): Promise<AdminUserDto> => {
  return apiClient.put<AdminUserDto>(`${API_BASE_URL}/${userId}/unban`, {});
};

/**
 * Delete a user (soft or hard delete)
 */
export const deleteUser = async (
  userId: number,
  deleteEntries: boolean,
  hardDelete: boolean,
  // When true the user ROW ITSELF is physically removed (irreversible)
  hardDeleteUser: boolean = false,
  // Optional admin note stored in the audit log
  reason: string = ""
): Promise<void> => {
  return apiClient.delete<void>(`${API_BASE_URL}/${userId}`, { deleteEntries, hardDelete, hardDeleteUser, reason });
};

// ==================== BIO MODERATION ====================

/**
 * Admin/mod edits a user's personal message (bio) directly.
 * Sends plain text in the body — empty string clears it.
 */
export const adminEditUserBio = async (
  userId: number,
  newBio: string
): Promise<void> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/bio`, {
    method: "PUT",
    headers: {
      "Content-Type": "text/plain",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: newBio,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || "Failed to edit bio");
  }
};

/**
 * Admin/mod deletes (clears) a user's personal message.
 */
export const adminDeleteUserBio = async (userId: number): Promise<void> => {
  return apiClient.delete<void>(`${API_BASE_URL}/${userId}/bio`, undefined);
};

/**
 * Admin/mod blocks a user from editing their bio.
 * Also clears the existing bio text on the backend.
 */
export const adminBlockUserBio = async (
  userId: number,
  reason?: string
): Promise<void> => {
  return apiClient.post<void>(`${API_BASE_URL}/${userId}/bio/block`, { reason: reason ?? null });
};

/**
 * Admin/mod lifts the bio block, allowing the user to set their message again.
 */
export const adminUnblockUserBio = async (userId: number): Promise<void> => {
  return apiClient.delete<void>(`${API_BASE_URL}/${userId}/bio/block`, undefined);
};

// ==================== BULK OPERATIONS ====================

/**
 * Bulk suspend users
 */
export const bulkSuspendUsers = async (
  userIds: number[],
  days: number,
  reason?: string
): Promise<BulkOperationResponse> => {
  return apiClient.post<BulkOperationResponse>(`${API_BASE_URL}/bulk/suspend`, {
    userIds,
    days,
    reason,
  });
};

/**
 * Bulk ban users (ADMIN only)
 */
export const bulkBanUsers = async (
  userIds: number[]
): Promise<BulkOperationResponse> => {
  return apiClient.post<BulkOperationResponse>(`${API_BASE_URL}/bulk/ban`, { userIds });
};

/**
 * Bulk change role (ADMIN only)
 */
export const bulkChangeRole = async (
  userIds: number[],
  newRole: UserRoleEnum
): Promise<BulkOperationResponse> => {
  return apiClient.post<BulkOperationResponse>(`${API_BASE_URL}/bulk/role`, {
    userIds,
    newRole,
  });
};

/**
 * Bulk delete users (ADMIN only)
 */
export const bulkDeleteUsers = async (
  userIds: number[],
  deleteEntries: boolean,
  hardDelete: boolean,
  // When true each user ROW ITSELF is physically removed (irreversible)
  hardDeleteUser: boolean = false,
  // Optional admin note stored in the audit log
  reason: string = ""
): Promise<BulkOperationResponse> => {
  return apiClient.delete<BulkOperationResponse>(`${API_BASE_URL}/bulk`, { userIds, deleteEntries, hardDelete, hardDeleteUser, reason });
};